import React, { createElement } from 'react';
import { DownloadIcon } from 'lucide-react';
type LogoVariant = {
  filename: string;
  label: string;
  description: string;
  bgClass: string;
  svg: string;
};
const logos: LogoVariant[] = [{
  filename: '888-sourcing-logo-horizontal.svg',
  label: 'Horizontal Logo (Dark)',
  description: 'Best for letterheads, email signatures, and any white or light background.',
  bgClass: 'bg-white',
  svg: `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 80" width="320" height="80">
  <rect x="4" y="4" width="72" height="72" rx="8" ry="8" fill="#c8924a"/>
  <text x="40" y="56" font-family="Inter, 'Helvetica Neue', Arial, sans-serif" font-size="32" font-weight="700" fill="#0a1628" text-anchor="middle" letter-spacing="-0.5">888</text>
  <text x="92" y="36" font-family="Inter, 'Helvetica Neue', Arial, sans-serif" font-size="18" font-weight="600" fill="#0a1628" letter-spacing="1.5">SOURCING</text>
  <text x="92" y="58" font-family="Inter, 'Helvetica Neue', Arial, sans-serif" font-size="18" font-weight="600" fill="#0a1628" letter-spacing="1.5">LIMITED</text>
</svg>`
}, {
  filename: '888-sourcing-logo-stacked.svg',
  label: 'Stacked Logo (Dark)',
  description: 'Best for business cards, social media profile images, and compact uses.',
  bgClass: 'bg-white',
  svg: `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 180" width="200" height="180">
  <rect x="64" y="4" width="72" height="72" rx="8" ry="8" fill="#c8924a"/>
  <text x="100" y="56" font-family="Inter, 'Helvetica Neue', Arial, sans-serif" font-size="32" font-weight="700" fill="#0a1628" text-anchor="middle" letter-spacing="-0.5">888</text>
  <text x="100" y="118" font-family="Inter, 'Helvetica Neue', Arial, sans-serif" font-size="20" font-weight="600" fill="#0a1628" text-anchor="middle" letter-spacing="2">SOURCING</text>
  <text x="100" y="148" font-family="Inter, 'Helvetica Neue', Arial, sans-serif" font-size="20" font-weight="600" fill="#0a1628" text-anchor="middle" letter-spacing="2">LIMITED</text>
</svg>`
}, {
  filename: '888-sourcing-logo-horizontal-white.svg',
  label: 'Horizontal Logo (Light)',
  description: 'Best for dark backgrounds — presentation slides, dark email signatures, etc.',
  bgClass: 'bg-navy-dark',
  svg: `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 80" width="320" height="80">
  <rect x="4" y="4" width="72" height="72" rx="8" ry="8" fill="#c8924a"/>
  <text x="40" y="56" font-family="Inter, 'Helvetica Neue', Arial, sans-serif" font-size="32" font-weight="700" fill="#0a1628" text-anchor="middle" letter-spacing="-0.5">888</text>
  <text x="92" y="36" font-family="Inter, 'Helvetica Neue', Arial, sans-serif" font-size="18" font-weight="600" fill="#ffffff" letter-spacing="1.5">SOURCING</text>
  <text x="92" y="58" font-family="Inter, 'Helvetica Neue', Arial, sans-serif" font-size="18" font-weight="600" fill="#ffffff" letter-spacing="1.5">LIMITED</text>
</svg>`
}];
function downloadSvg(svg: string, filename: string) {
  const blob = new Blob([svg], {
    type: 'image/svg+xml;charset=utf-8'
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
function downloadPng(svg: string, filename: string, scale = 4) {
  const img = new Image();
  const svgBlob = new Blob([svg], {
    type: 'image/svg+xml;charset=utf-8'
  });
  const url = URL.createObjectURL(svgBlob);
  img.onload = () => {
    const canvas = document.createElement('canvas');
    canvas.width = img.width * scale;
    canvas.height = img.height * scale;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.scale(scale, scale);
    ctx.drawImage(img, 0, 0);
    URL.revokeObjectURL(url);
    canvas.toBlob(blob => {
      if (!blob) return;
      const pngUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = pngUrl;
      link.download = filename.replace('.svg', '.png');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(pngUrl);
    }, 'image/png');
  };
  img.src = url;
}
export function LogoDownload() {
  return <div className="pt-24 pb-24 bg-navy-dark min-h-screen" data-id="element-500">
      <section className="py-12 px-6" data-id="element-501">
        <div className="max-w-4xl mx-auto" data-id="element-502">
          <div className="text-center mb-12" data-id="element-503">
            <h1 className="text-3xl lg:text-4xl font-semibold text-white mb-4" data-id="element-504">
              Brand Assets
            </h1>
            <p className="text-slate-400 max-w-2xl mx-auto" data-id="element-505">
              Download your company logo for letterheads, business cards,
              presentations, and other materials. SVG is best for print, PNG is
              best for social media and email signatures.
            </p>
          </div>

          <div className="space-y-8" data-id="element-506">
            {logos.map(logo => <div key={logo.filename} className="bg-navy-light border border-slate-800 rounded-lg overflow-hidden" data-id="element-507">
                <div className={`${logo.bgClass} p-12 flex items-center justify-center min-h-[200px]`} data-id="element-508">
                  <div dangerouslySetInnerHTML={{
                __html: logo.svg
              }} data-id="element-509" />
                </div>
                <div className="p-6" data-id="element-510">
                  <h2 className="text-lg font-semibold text-white mb-1" data-id="element-511">
                    {logo.label}
                  </h2>
                  <p className="text-slate-400 text-sm mb-4" data-id="element-512">
                    {logo.description}
                  </p>
                  <div className="flex flex-wrap gap-3" data-id="element-513">
                    <button onClick={() => downloadSvg(logo.svg, logo.filename)} className="inline-flex items-center gap-2 bg-gold hover:bg-gold-dark text-navy-dark px-4 py-2 rounded-md text-sm font-semibold transition-colors" data-id="element-514">
                      <DownloadIcon size={16} data-id="element-515" />
                      Download SVG
                    </button>
                    <button onClick={() => downloadPng(logo.svg, logo.filename, 4)} className="inline-flex items-center gap-2 border-2 border-slate-700 hover:border-gold text-white px-4 py-2 rounded-md text-sm font-semibold transition-colors" data-id="element-516">
                      <DownloadIcon size={16} data-id="element-517" />
                      Download PNG (high-res)
                    </button>
                  </div>
                </div>
              </div>)}
          </div>

          <div className="mt-12 bg-navy-light border-l-4 border-gold p-6 rounded-md" data-id="element-518">
            <h3 className="text-white font-semibold mb-2" data-id="element-519">Quick guide</h3>
            <ul className="text-slate-400 text-sm space-y-2 list-disc pl-5" data-id="element-520">
              <li data-id="element-521">
                <strong className="text-slate-200" data-id="element-522">SVG</strong> — vector format,
                scales without losing quality. Use for letterheads, print
                materials, and anywhere you need crisp edges at any size.
              </li>
              <li data-id="element-523">
                <strong className="text-slate-200" data-id="element-524">PNG</strong> — image format
                with transparent background. Use for email signatures, social
                media, presentations, and platforms that don't support SVG.
              </li>
              <li data-id="element-525">
                For business cards or professional printing, send the SVG
                directly to your printer.
              </li>
            </ul>
          </div>
        </div>
      </section>
    </div>;
}